/* eslint-disable no-restricted-syntax -- file defines overrides */
import { normalizePathname } from 'shared/helpers/hooks/usePath';
import { trackNavigationEvent } from 'shared/helpers/hooks/useTrackPerformance';

import { globalTrackEvent } from './observability';

const trackHardNavEvent = (
  action: 'navigate' | 'redirect',
  label: string | URL,
  data?: Parameters<typeof globalTrackEvent>[2],
  options?: Parameters<typeof globalTrackEvent>[3],
) => {
  const pathname = window.location.pathname;
  const path = normalizePathname(pathname);
  globalTrackEvent(
    action,
    typeof label === 'string' ? label : label.pathname,
    { ...data, from: pathname, path, pathname },
    options,
  );
  trackNavigationEvent();
};

type TrackEventCustomArgs = { label?: string; data?: Parameters<typeof globalTrackEvent>[2] };
/**
 * Helper function to wrap replacing the current page.
 * Enables metrics + easier stubbing in tests
 * @throws {SystemError} - If the function is called in a non-browser environment
 *
 * NOTE: This function will not return, it will replace the current page with the new URL. It triggers a hard navigation which will kick off a fresh page load and fetch ALL relevant queries and resources
 */
const hardNavReplace = (url: string | URL, args?: TrackEventCustomArgs) => {
  if (typeof window === 'undefined') {
    return;
  }
  trackHardNavEvent('redirect', args?.label ?? url, args?.data);
  window.location.replace(url);
};

/**
 * Helper function to wrap navigating to a new page.
 * Enables metrics + easier stubbing in tests
 * @throws {SystemError} - If the function is called in a non-browser environment
 *
 * NOTE: This function will not return, it will navigates to the new URL. It triggers a hard navigation which will kick off a fresh page load and fetch ALL relevant queries and resources
 */
const hardNavAssign = (url: string | URL, args?: TrackEventCustomArgs) => {
  if (typeof window === 'undefined') {
    return;
  }
  trackHardNavEvent('navigate', args?.label ?? url, args?.data);
  window.location.assign(url);
};

/**
 * Helper function to wrap reloading the page.
 * Enables metrics + easier stubbing in tests
 * @throws {SystemError} - If the function is called in a non-browser environment
 *
 * NOTE: This function will not return, it will reloads the current page. It triggers a hard navigation which will kick off a fresh page load and fetch ALL relevant queries and resources
 */
const hardPageReload = () => {
  if (typeof window === 'undefined') {
    return;
  }
  trackHardNavEvent('navigate', 'reload');
  window.location.reload();
};

// eslint-disable-next-line import/no-default-export, import/no-anonymous-default-export -- allow default exports for helper file
export default {
  hardNavAssign,
  hardNavReplace,
  hardPageReload,
};
